"use client";

import { c as _c } from "react/compiler-runtime";
import { IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import type { ReactNode } from "react";
export type ToastMetaTone = "default" | "success" | "info" | "warning" | "danger";
export interface ToastMetaItem {
  label: string;
  tone?: ToastMetaTone;
}
export interface ToastLinkItem {
  label: string;
  href: string;
  external?: boolean;
}
export interface ToastButtonItem {
  label: string;
  href: string;
  external?: boolean;
  variant?: "brand" | "black";
}
interface ToastDetailCardProps {
  toastId: string;
  summary?: ReactNode;
  description?: ReactNode;
  meta?: ToastMetaItem[];
  links?: ToastLinkItem[];
  button?: ToastButtonItem;
}

// Text is forced white in both modes because the underlying `<Button
// variant="black">` paints a dark gradient background — using
// `text-ds-text-secondary` (which flips to near-black in light mode) made
// the "Open workflow" label invisible on the black button (@faith's bug).
const grayToastButtonClassName = "text-white [--bg-color:theme(colors.neutral.200)] [--bg-color-active:theme(colors.neutral.300)] [--shadow-light:theme(colors.white)] [--shadow-dark:theme(colors.neutral.400)] dark:[--bg-color:theme(colors.neutral.600)] dark:[--bg-color-active:theme(colors.neutral.700)] dark:[--shadow-light:theme(colors.neutral.400)] dark:[--shadow-dark:theme(colors.neutral.800)]";
const blueToastButtonClassName = "[--bg-color:theme(colors.b3-blue)] [--bg-color-active:#1a3fd9] [--shadow-light:#5a7eff] [--shadow-dark:#11172E] dark:[--bg-color:theme(colors.b3-blue)] dark:[--bg-color-active:#1a3fd9] dark:[--shadow-light:#5a7eff] dark:[--shadow-dark:#11172E]";
function getToastButtonClassName(variant: ToastButtonItem["variant"]) {
  return variant === "brand" ? blueToastButtonClassName : grayToastButtonClassName;
}
interface ToastActionButtonProps {
  toastId: string;
  label: string;
  href: string;
  external?: boolean;
  variant?: "brand" | "black";
  showChevron?: boolean;
}
function ToastActionButton(t0) {
  const $ = _c(25);
  const {
    toastId,
    label,
    href,
    external: t1,
    variant: t2,
    showChevron: t3
  } = t0;
  const external = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "black" : t2;
  const showChevron = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== showChevron) {
    t4 = showChevron ? <IconChevronRightOutline18 className="h-3 w-3" /> : null;
    $[0] = showChevron;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== label || $[3] !== t4) {
    t5 = <span className="inline-flex items-center gap-1">{label}{t4}</span>;
    $[2] = label;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const content = t5;
  if (external) {
    let t6;
    if ($[5] !== variant) {
      t6 = getToastButtonClassName(variant);
      $[5] = variant;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== href || $[8] !== toastId) {
      t7 = () => {
        toast.dismiss(toastId);
        window.open(href, "_blank", "noopener,noreferrer");
      };
      $[7] = href;
      $[8] = toastId;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== content || $[11] !== t6 || $[12] !== t7 || $[13] !== variant) {
      t8 = <Button variant={variant} size="xs" className={t6} onClick={t7}>{content}</Button>;
      $[10] = content;
      $[11] = t6;
      $[12] = t7;
      $[13] = variant;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t6;
  if ($[15] !== variant) {
    t6 = getToastButtonClassName(variant);
    $[15] = variant;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== href || $[18] !== toastId) {
    t7 = () => {
      toast.dismiss(toastId);
      const targetPath = new URL(href, window.location.origin).pathname;
      if (targetPath === window.location.pathname) {
        window.history.pushState(null, "", href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } else {
        window.location.href = href;
      }
    };
    $[17] = href;
    $[18] = toastId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== content || $[21] !== t6 || $[22] !== t7 || $[23] !== variant) {
    t8 = <Button variant={variant} size="xs" className={t6} onClick={t7}>{content}</Button>;
    $[20] = content;
    $[21] = t6;
    $[22] = t7;
    $[23] = variant;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
export function ToastDetailCard(t0) {
  const $ = _c(18);
  const {
    toastId,
    summary,
    description,
    meta: t1,
    links: t2,
    button
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const meta = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const links = t4;
  const hasFooter = links.length > 0 || Boolean(button);
  let t5;
  if ($[4] !== meta || $[5] !== summary) {
    t5 = summary || meta.length > 0 ? <div className="flex min-w-0 items-center gap-1.5">{summary ? <div className="body-sm-semibold min-w-0 flex-1 truncate" style={{
        color: "var(--toast-title-color)"
      }}>{summary}</div> : null}{meta.map(_temp)}</div> : null;
    $[4] = meta;
    $[5] = summary;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== description) {
    t6 = description ? <div className="body-sm line-clamp-2" style={{
      color: "var(--toast-body-color)"
    }}>{description}</div> : null;
    $[7] = description;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== button || $[10] !== hasFooter || $[11] !== links || $[12] !== toastId) {
    t7 = hasFooter ? <div className="flex flex-wrap items-center gap-1.5 pt-2">{button ? <ToastActionButton toastId={toastId} label={button.label} href={button.href} external={button.external} variant={button.variant ?? "black"} showChevron={true} /> : null}{links.map(link => <ToastActionButton key={`${link.href}-${link.label}`} toastId={toastId} label={link.label} href={link.href} external={link.external} />)}</div> : null;
    $[9] = button;
    $[10] = hasFooter;
    $[11] = links;
    $[12] = toastId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className="space-y-1">{t5}{t6}{t7}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp(item) {
  return <span key={item.label} className="text-3xs inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 font-medium uppercase tracking-wider" style={{
    backgroundColor: "color-mix(in srgb, var(--toast-muted-color) 15%, transparent)",
    color: "var(--toast-muted-color)"
  }}>{item.label}</span>;
}